import {
  editingSupported,
  RigDetailsComputedFormState,
  RigDetailsFormState,
} from "./FormState";
import { getRobotPath } from "portal/utils/routing";
import { Header } from "portal/components/header/Header";
import { ImplementConfiguration } from "portal/utils/atkEquipment";
import { Page } from "portal/components/Page";
import { Paper, Typography } from "@mui/material";
import { RigDetailsHeader } from "portal/views/autotractor/rig-details/RigDetailsHeader";
import { RigDetailsSwitchViewCallback } from "../RigDetailsPage";
import { RigStatus } from "./RigStatus";
import { RobotSummaryResponse } from "protos/portal/robots";
import { ScvInput, ScvOutput } from "protos/atk/autotractor";
import {
  ScvInputDetailView,
  ScvInputListItem,
} from "./RigConnections.ScvInput";
import {
  ScvOutputDetailView,
  ScvOutputListItem,
} from "./RigConnections.ScvOutput";
import { titleCase, valueOrLoading } from "portal/utils/strings";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import DoneIcon from "@mui/icons-material/CheckCircleOutlined";
import React, { useContext, useMemo, useState } from "react";

interface RigConnectionsProps {
  switchToView: RigDetailsSwitchViewCallback;
  summary: RobotSummaryResponse | undefined;
}

type ImplementAndInput = { implement: ImplementConfiguration; input: ScvInput };

type RigConnectionsSelection =
  | { scvInput: ImplementAndInput | undefined; scvOutput?: undefined }
  | { scvInput?: undefined; scvOutput: ScvOutput | undefined };

export const RigConnections: React.FC<RigConnectionsProps> = ({
  switchToView,
  summary,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { values } = useFormikContext<RigDetailsFormState>();
  const {
    resolvedTractorConfig,
    resolvedImplementConfigs,
    scvOutputsById,
    scvInputsById,
  } = useContext(RigDetailsComputedFormState);

  let name: string = "";
  if (summary) {
    name = valueOrLoading(summary.robot?.customerSerial, t);
  }

  const connectionMap = useMemo(() => {
    const m: {
      inputsForOutput: { [outputId: string]: ScvInput[] };
      outputForInput: { [inputId: string]: ScvOutput };
    } = { inputsForOutput: {}, outputForInput: {} };

    for (const connection of Object.values(values.scvConnectionsByInput)) {
      // update inputs-for-output map
      const connectedInputs =
        m.inputsForOutput[connection.tractorScvOutputId] ?? [];
      const input = scvInputsById[connection.implementScvInputId];

      if (input) {
        connectedInputs.push(input);
      }

      m.inputsForOutput[connection.tractorScvOutputId] = connectedInputs;

      // update output-for-input map
      const output = scvOutputsById[connection.tractorScvOutputId];

      if (output) {
        m.outputForInput[connection.implementScvInputId] = output;
      }
    }

    return m;
  }, [values.scvConnectionsByInput, scvInputsById, scvOutputsById]);

  const [selection, setSelection] = useState<RigConnectionsSelection>({
    scvInput: undefined,
    scvOutput: undefined,
  });

  const scvOutputs = resolvedTractorConfig?.scvOutputs?.scvOutput ?? [];

  return (
    <>
      <Header hideTitle={true} title={name} />
      <Page>
        <div className="flex flex-col gap-8">
          <RigDetailsHeader
            breadcrumbs={[
              {
                text: name,
                onClick: () => navigate(getRobotPath(values.serial)),
              },
              {
                text:
                  values.name === ""
                    ? t("models.equipment.noName")
                    : values.name,
                onClick: () => switchToView("status-and-config"),
              },
            ]}
            headline={"connections"}
            actionButtons={[
              {
                startIcon: <DoneIcon />,
                text: t("utils.actions.apply"),
                onClick: () => switchToView("status-and-config"),
              },
            ]}
            readOnly={!editingSupported(values)}
          />
          <div className="flex flex-row gap-4">
            <div className="flex flex-col gap-4 flex-1">
              <Paper className="rounded-lg col-start-1">
                {resolvedTractorConfig ? (
                  <>
                    <Typography className="text-xl mt-4 mx-4">
                      <strong>
                        {titleCase(t("models.equipment.tractor", { count: 1 }))}
                        :
                      </strong>{" "}
                      {resolvedTractorConfig.name}
                    </Typography>
                    <Typography
                      color="secondary"
                      className="text-lg italic mx-4"
                    >
                      {resolvedTractorConfig.fullModelName}
                    </Typography>
                    <Typography variant="h6" className="mx-4 mt-4">
                      {t("models.equipment.scv_short", {
                        count: scvOutputs.length,
                      })}
                    </Typography>
                    <ul className="p-0 divide-y divide-x-0 divide-solid divide-gray-400">
                      {scvOutputs.map((scvOutput, index) => (
                        <ScvOutputListItem
                          key={index}
                          output={scvOutput}
                          connectedInputs={
                            connectionMap.inputsForOutput[scvOutput.id] ?? []
                          }
                          selection={selection}
                          setSelection={setSelection}
                        />
                      ))}
                    </ul>
                  </>
                ) : (
                  <Typography className="text-xl m-4">
                    <strong>
                      {titleCase(t("models.equipment.tractor", { count: 1 }))}:{" "}
                    </strong>
                    {t("utils.descriptors.none")}
                  </Typography>
                )}
              </Paper>
              {resolvedImplementConfigs.length > 0 ? (
                resolvedImplementConfigs.map((implement, index) => {
                  const scvInputs = implement.scvInputs?.inputs ?? [];

                  return (
                    <Paper key={index} className="rounded-lg col-start-1">
                      <Typography className="mx-4 mt-4 text-xl">
                        <strong>
                          {titleCase(
                            t("models.equipment.implement", { count: 1 })
                          )}
                          :
                        </strong>{" "}
                        {implement.name}
                      </Typography>
                      <Typography
                        color="secondary"
                        className="text-lg italic mx-4"
                      >
                        {implement.fullModelName}
                      </Typography>
                      <Typography variant="h6" className="mx-4 mt-4">
                        {t("models.equipment.scvInput", {
                          count: scvInputs.length,
                        })}
                      </Typography>
                      <ul className="p-0 divide-y divide-x-0 divide-solid divide-gray-400">
                        {scvInputs.map((scvInput, index) => (
                          <ScvInputListItem
                            key={index}
                            implement={implement}
                            input={scvInput}
                            connectedOutput={
                              connectionMap.outputForInput[scvInput.id]
                            }
                            selection={selection}
                            setSelection={setSelection}
                          />
                        ))}
                      </ul>
                    </Paper>
                  );
                })
              ) : (
                <Paper className="p-4 rounded-lg">
                  <Typography className="text-xl">
                    <strong>
                      {titleCase(t("models.equipment.implement", { count: 0 }))}
                      :
                    </strong>{" "}
                    {t("utils.descriptors.none")}
                  </Typography>
                </Paper>
              )}
            </div>
            <div className="flex flex-col flex-1 gap-4">
              {selection.scvInput && (
                <ScvInputDetailView
                  availableOutputs={
                    resolvedTractorConfig?.scvOutputs?.scvOutput ?? []
                  }
                  {...selection.scvInput}
                />
              )}
              {selection.scvOutput && (
                <ScvOutputDetailView output={selection.scvOutput} />
              )}
              <RigStatus />
            </div>
          </div>
        </div>
      </Page>
    </>
  );
};
